<p-toast></p-toast>
<p-progressBar mode="indeterminate" *ngIf="isLoading"></p-progressBar>

<app-page-shell *ngIf="!isDialogMode; else dialogMasterForm">
  <app-page-hero
    [eyebrow]="isStandaloneContext ? 'Operations Setup' : 'Warehousing Setup'"
    [title]="isEditMode ? 'Edit ' + title : 'New ' + title"
    [subtitle]="setupDescription"
    [backLink]="listRoute">
    <div actions>
      <p-button label="Save Setup Value" icon="pi pi-save" type="button" [loading]="isSaving" [disabled]="isSaving" (click)="saveMasterData()"></p-button>
    </div>
  </app-page-hero>

  <ng-container *ngTemplateOutlet="masterFormTemplate"></ng-container>
</app-page-shell>

<ng-template #dialogMasterForm>
  <div class="p-3">
    <ng-container *ngTemplateOutlet="masterFormTemplate"></ng-container>
  </div>
</ng-template>

<ng-template #masterFormTemplate>
  <form [formGroup]="masterForm" (ngSubmit)="saveMasterData()" class="warehousing-form-grid p-fluid">
    <app-page-form-section icon="pi pi-database" title="Setup Value" [subtitle]="setupDescription">
      <div class="formgrid grid">
        <div class="field col-12 md:col-8"><p-inputGroup><p-inputGroupAddon><i class="pi pi-tag"></i></p-inputGroupAddon><p-iftaLabel><input id="name" pInputText formControlName="name"><label for="name">{{ nameLabel }}</label></p-iftaLabel></p-inputGroup></div>
        <div class="field col-12 md:col-4"><p-inputGroup><p-inputGroupAddon><i class="pi pi-hashtag"></i></p-inputGroupAddon><p-iftaLabel><input id="code" pInputText formControlName="code" [readOnly]="isWarehouse" [placeholder]="isWarehouse && !isEditMode ? 'Generated on save' : ''"><label for="code">{{ codeLabel }}</label></p-iftaLabel></p-inputGroup><small *ngIf="isWarehouse" class="text-500">Generated from the name and linked location city. The code stays the same after renaming.</small></div>
        <div class="field col-12 md:col-8" *ngIf="type === 'REGION'">
          <p-inputGroup>
            <p-inputGroupAddon><i class="pi pi-warehouse"></i></p-inputGroupAddon>
            <p-iftaLabel>
              <p-select inputId="defaultWarehouseId" formControlName="defaultWarehouseId" [options]="warehouseOptions" optionLabel="name" optionValue="id" [filter]="true" filterBy="name,code,parentName,locationName" [showClear]="true" styleClass="w-full">
                <ng-template pTemplate="selectedItem" let-row>
                  <span>{{ row?.name || row?.code || 'Default warehouse optional' }}</span>
                </ng-template>
                <ng-template pTemplate="item" let-row>
                  <div class="warehouse-option">
                    <strong>{{ row.name || row.code || ('Warehouse #' + row.id) }}</strong>
                    <span>{{ warehouseOptionSubtitle(row) }}</span>
                  </div>
                </ng-template>
              </p-select>
              <label for="defaultWarehouseId">Default Cross-Dock Warehouse Optional</label>
            </p-iftaLabel>
          </p-inputGroup>
          <small class="text-500">Used as the regional hub for cross-dock routing. One warehouse can be assigned as default for multiple regions.</small>
        </div>
        <div class="col-12" *ngIf="type === 'REGION'">
          <section class="region-mapping-panel">
            <div class="region-mapping-header">
              <div>
                <div class="region-mapping-title">Covered Cities</div>
                <div class="region-mapping-subtitle">Assign one region to multiple cities with explicit country and city coverage.</div>
              </div>
              <div class="region-mapping-header-actions">
                <div class="region-mapping-stat">
                  <span>{{ regionMappingCount }}</span>
                  <small>cities</small>
                </div>
                <div class="region-mapping-stat">
                  <span>{{ uniqueRegionCountryCount }}</span>
                  <small>countries</small>
                </div>
                <p-button type="button" icon="pi pi-plus" label="Add City" severity="secondary" [outlined]="true" (click)="addRegionMapping()"></p-button>
              </div>
            </div>

            @if (!regionMappings.length) {
              <div class="region-mapping-empty">
                <i class="pi pi-map-marker"></i>
                <div>
                  <strong>No city coverage added</strong>
                  <span>Add the cities this region should cover.</span>
                </div>
              </div>
            }

            <div formArrayName="regionMappings" class="region-mapping-list">
            @for (mapping of regionMappings.controls; track $index) {
              <div class="region-mapping-card" [formGroupName]="$index">
                <div class="region-mapping-card-head">
                  <div class="region-mapping-card-title">
                    <span class="region-mapping-index">{{ $index + 1 }}</span>
                    <div>
                      <strong>{{ regionMappingCityName($index) }}</strong>
                      <small>{{ regionMappingCountryName($index) }}</small>
                    </div>
                  </div>
                  <p-button type="button" icon="pi pi-trash" severity="danger" [text]="true" (click)="removeRegionMapping($index)"></p-button>
                </div>

                <div class="grid align-items-start">
                  <div class="field col-12 lg:col-6">
                    <p-inputGroup>
                      <p-inputGroupAddon><i class="pi pi-globe"></i></p-inputGroupAddon>
                      <p-iftaLabel>
                        <p-select [inputId]="'regionCountry_' + $index" formControlName="countryId" [options]="countries" optionLabel="name" optionValue="id" [filter]="true" filterBy="name,countryCode" [showClear]="true" styleClass="w-full" (onChange)="onRegionMappingCountryChange($index)"></p-select>
                        <label [for]="'regionCountry_' + $index">Country</label>
                      </p-iftaLabel>
                    </p-inputGroup>
                    @if (mapping.get('countryId')?.invalid && mapping.get('countryId')?.touched) { <small class="p-error">Country is required.</small> }
                  </div>
                  <div class="field col-12 lg:col-6">
                    <p-inputGroup>
                      <p-inputGroupAddon><i class="pi pi-map-marker"></i></p-inputGroupAddon>
                      <p-iftaLabel>
                        <p-select [inputId]="'regionCity_' + $index" formControlName="cityId" [options]="cityOptionsFor($index)" optionLabel="cityName" optionValue="id" [filter]="true" filterBy="cityName" [showClear]="true" styleClass="w-full"></p-select>
                        <label [for]="'regionCity_' + $index">City</label>
                      </p-iftaLabel>
                    </p-inputGroup>
                    @if (mapping.get('cityId')?.invalid && mapping.get('cityId')?.touched) { <small class="p-error">City is required.</small> }
                  </div>
                </div>
              </div>
            }
            </div>
          </section>
        </div>
        <div class="field col-12 md:col-4" *ngIf="isWarehouse">
          <p-inputGroup>
            <p-inputGroupAddon><i class="pi pi-map-marker"></i></p-inputGroupAddon>
            <p-iftaLabel>
              <p-select inputId="locationId" formControlName="locationId" appLazyLocationPicker optionLabel="name" optionValue="id" [filter]="true" filterBy="name" [showClear]="false" styleClass="w-full"></p-select>
              <label for="locationId">Linked Location</label>
            </p-iftaLabel>
            <p-inputGroupAddon class="p-0">
              <p-button type="button" icon="pi pi-plus" severity="secondary" [text]="true" (click)="openLocationDialog()"></p-button>
            </p-inputGroupAddon>
          </p-inputGroup>
          @if (masterForm.get('locationId')?.invalid && masterForm.get('locationId')?.touched) { <small class="p-error">Linked location is required.</small> }
        </div>
        <div class="field col-12 md:col-4" *ngIf="type === 'WAREHOUSE'">
          <p-inputGroup><p-inputGroupAddon><i class="pi pi-map"></i></p-inputGroupAddon><p-iftaLabel><p-select inputId="parentRegionId" formControlName="parentId" [options]="masterData.REGION" optionLabel="name" optionValue="id" [filter]="true" filterBy="name,code" [showClear]="false" styleClass="w-full"></p-select><label for="parentRegionId">Region</label></p-iftaLabel></p-inputGroup>
          @if (masterForm.get('parentId')?.invalid && masterForm.get('parentId')?.touched) { <small class="p-error">Region is required.</small> }
        </div>
        <div class="field col-12 md:col-4" *ngIf="type === 'WAREHOUSE'">
          <p-inputGroup><p-inputGroupAddon><i class="pi pi-truck"></i></p-inputGroupAddon><p-iftaLabel><p-select inputId="supplierClientId" formControlName="supplierClientId" [options]="suppliers" optionLabel="companyName" optionValue="id" [filter]="true" filterBy="companyName,clientNumber" [showClear]="true" styleClass="w-full"></p-select><label for="supplierClientId">{{ supplierLabel() }}</label></p-iftaLabel></p-inputGroup>
          @if (masterForm.get('supplierClientId')?.invalid && masterForm.get('supplierClientId')?.touched) { <small class="p-error">A custodian supplier is required for third-party warehouses.</small> }
        </div>
        <div class="field col-12 md:col-4" *ngIf="type === 'WAREHOUSE'">
          <p-inputGroup>
            <p-inputGroupAddon><i class="pi pi-shield"></i></p-inputGroupAddon>
            <p-iftaLabel>
              <p-select
                inputId="warehouseOwnershipType"
                formControlName="warehouseOwnershipType"
                [options]="[
                  { label: 'Company Owned', value: 'COMPANY' },
                  { label: 'Third-Party Virtual (non-stock)', value: 'THIRD_PARTY_VIRTUAL' },
                  { label: 'Third-Party Managed Cold Store', value: 'THIRD_PARTY_MANAGED' }
                ]"
                optionLabel="label"
                optionValue="value"
                styleClass="w-full"></p-select>
              <label for="warehouseOwnershipType">Ownership Type</label>
            </p-iftaLabel>
          </p-inputGroup>
        </div>
        <div class="field col-12 md:col-8" *ngIf="type === 'WAREHOUSE'">
          <p-inputGroup><p-inputGroupAddon><i class="pi pi-building"></i></p-inputGroupAddon><p-iftaLabel><input id="supplierWarehouseName" pInputText formControlName="supplierWarehouseName"><label for="supplierWarehouseName">Supplier Warehouse Name Optional</label></p-iftaLabel></p-inputGroup>
        </div>
        <div class="field col-12 md:col-4" *ngIf="type === 'ZONE'">
          <p-inputGroup><p-inputGroupAddon><i class="pi pi-warehouse"></i></p-inputGroupAddon><p-iftaLabel><p-select inputId="parentWarehouseId" formControlName="parentId" [options]="warehouseOptions" optionLabel="name" optionValue="id" [filter]="true" filterBy="name,code" [showClear]="true" styleClass="w-full"></p-select><label for="parentWarehouseId">Warehouse</label></p-iftaLabel></p-inputGroup>
        </div>
        <div class="field col-12 md:col-4" *ngIf="type === 'CATEGORY'">
          <p-inputGroup><p-inputGroupAddon><i class="pi pi-building"></i></p-inputGroupAddon><p-iftaLabel><p-select inputId="crmClientId" formControlName="crmClientId" [options]="clients" optionLabel="companyName" optionValue="id" [filter]="true" filterBy="companyName" [showClear]="true" styleClass="w-full"></p-select><label for="crmClientId">CRM Client</label></p-iftaLabel></p-inputGroup>
        </div>
        <div class="field col-12 md:col-4" *ngIf="type === 'CATEGORY'">
          <p-inputGroup><p-inputGroupAddon><i class="pi pi-sitemap"></i></p-inputGroupAddon><p-iftaLabel><p-select inputId="crmAccountId" formControlName="crmAccountId" [options]="filteredAccounts(masterForm.value.crmClientId)" optionLabel="companyName" optionValue="id" [filter]="true" filterBy="companyName" [showClear]="true" styleClass="w-full"></p-select><label for="crmAccountId">Client Account</label></p-iftaLabel></p-inputGroup>
        </div>
        <div class="field col-12 md:col-4" *ngIf="type === 'CATEGORY'">
          <p-inputGroup><p-inputGroupAddon><i class="pi pi-folder"></i></p-inputGroupAddon><p-iftaLabel><p-select inputId="parentCategoryId" formControlName="parentId" [options]="categoryOptions()" optionLabel="name" optionValue="id" [filter]="true" filterBy="name,code,hierarchyPath" [showClear]="true" styleClass="w-full"></p-select><label for="parentCategoryId">Parent Category Optional</label></p-iftaLabel></p-inputGroup>
        </div>
        <div class="field col-12 md:col-4" *ngIf="type === 'BRAND'">
          <p-inputGroup><p-inputGroupAddon><i class="pi pi-folder-open"></i></p-inputGroupAddon><p-iftaLabel><p-select inputId="categoryId" formControlName="categoryId" [options]="categoryOptions()" optionLabel="name" optionValue="id" [filter]="true" filterBy="name,code,hierarchyPath" styleClass="w-full"></p-select><label for="categoryId">Category</label></p-iftaLabel></p-inputGroup>
          @if (masterForm.get('categoryId')?.invalid && masterForm.get('categoryId')?.touched) { <small class="p-error">Category is required.</small> }
        </div>
        <div class="field col-12 md:col-4" *ngIf="type === 'SUB_BRAND'">
          <p-inputGroup><p-inputGroupAddon><i class="pi pi-star"></i></p-inputGroupAddon><p-iftaLabel><p-select inputId="brandId" formControlName="brandId" [options]="masterData.BRAND" optionLabel="name" optionValue="id" [filter]="true" filterBy="name,code,hierarchyPath" styleClass="w-full"></p-select><label for="brandId">Brand</label></p-iftaLabel></p-inputGroup>
          @if (masterForm.get('brandId')?.invalid && masterForm.get('brandId')?.touched) { <small class="p-error">Brand is required.</small> }
        </div>
        <div class="field col-12 md:col-4 flex align-items-center gap-2"><p-checkbox inputId="isActive" formControlName="isActive" [binary]="true"></p-checkbox><label for="isActive" class="font-medium">Active</label></div>
      </div>
    </app-page-form-section>
  </form>
  @if (isDialogMode) {
    <div class="flex justify-content-end gap-2 mt-3">
      <p-button label="Cancel" icon="pi pi-times" severity="secondary" [outlined]="true" type="button" (click)="discard()"></p-button>
      <p-button label="Save Setup Value" icon="pi pi-save" type="button" [loading]="isSaving" [disabled]="isSaving" (click)="saveMasterData()"></p-button>
    </div>
  }
</ng-template>
<app-party-quick-create-dialog
  [(visible)]="quickCreateVisible"
  type="location"
  [countries]="countryOptions()"
  [owningCompany]="currentCompany()"
  [defaultCountryId]="defaultCountryId()"
  (created)="onQuickCreateCreated($event)"
  (failed)="quickCreateFailed($event)">
</app-party-quick-create-dialog>
